/**
 * AdminLeadPacksPanel — Admin-only UI to edit:
 *   1. Lead-pack CATALOG (sku / scope / qty / price / label).
 *   2. Calendar Monthly Lift Pass price.
 *
 * Backend:
 *   GET/PUT /api/admin/lead-packs/catalog
 *   GET/PUT /api/admin/calendar-lift-monthly-pass
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingBag, Plus, Trash, FloppyDisk, CalendarCheck,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCOPES = ['wedding', 'all_events'];

const blank = () => ({
  sku: '',
  scope: 'all_events',
  qty: 30,
  price_inr: 499,
  label: '',
});

const AdminLeadPacksPanel = () => {
  const [catalog, setCatalog] = useState([]);
  const [passPrice, setPassPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingCat, setSavingCat] = useState(false);
  const [savingPass, setSavingPass] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/lead-packs/catalog`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/calendar-lift-monthly-pass`, { withCredentials: true }),
      ]);
      setCatalog(c.data.catalog || []);
      setPassPrice(p.data.calendar_lift_monthly_pass_inr || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (i, patch) =>
    setCatalog((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removeRow = (i) =>
    setCatalog((rows) => rows.filter((_, idx) => idx !== i));

  const addRow = () =>
    setCatalog((rows) => [...rows, blank()]);

  const saveCatalog = async () => {
    setSavingCat(true);
    try {
      const cleaned = catalog.map((r) => ({
        sku: String(r.sku || '').trim(),
        scope: SCOPES.includes(r.scope) ? r.scope : 'all_events',
        qty: Number(r.qty) || 0,
        price_inr: Number(r.price_inr) || 0,
        label: String(r.label || '').trim() || `${r.scope} · ${r.qty}`,
      })).filter((r) => r.sku && r.qty > 0 && r.price_inr > 0);
      if (cleaned.length === 0) {
        toast.error('At least one valid pack is required');
        return;
      }
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/lead-packs/catalog`,
        { catalog: cleaned },
        { withCredentials: true },
      );
      setCatalog(data.catalog || cleaned);
      toast.success('Catalog saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingCat(false);
    }
  };

  const saveMonthlyPass = async () => {
    setSavingPass(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/calendar-lift-monthly-pass`,
        { calendar_lift_monthly_pass_inr: Number(passPrice) || 0 },
        { withCredentials: true },
      );
      setPassPrice(data.calendar_lift_monthly_pass_inr);
      toast.success('Monthly lift pass price saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPass(false);
    }
  };

  if (loading) return <p className="text-sm text-text-secondary py-4">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="admin-lead-packs-panel">
      {/* Monthly Lift Pass price */}
      <Card className="bg-white border-[#E8E1D5]">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <CalendarCheck size={20} className="text-emerald-700" weight="duotone" /> Calendar · Monthly Lift Pass
          </CardTitle>
          <CardDescription>
            One flat price per vendor / pro / venue. When active, every system-busy date
            for the current month becomes free to lift (no per-day charge).
            Existing per-day lift fee continues to apply when the pass is not active.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-[200px]">
              <Label htmlFor="monthly-pass-price" className="text-xs uppercase tracking-wider font-bold text-text-secondary">
                Price (₹)
              </Label>
              <Input
                id="monthly-pass-price"
                type="number" min={0}
                value={passPrice}
                onChange={(e) => setPassPrice(e.target.value)}
                className="rounded-none"
                data-testid="monthly-pass-price-input"
              />
            </div>
            <Button onClick={saveMonthlyPass} disabled={savingPass}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-monthly-pass-btn">
              <FloppyDisk size={14} className="mr-1.5" /> {savingPass ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lead Pack catalog editor */}
      <Card className="bg-white border-[#E8E1D5]">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <ShoppingBag size={20} className="text-brand-primary" weight="duotone" /> Lead Pack Catalog
          </CardTitle>
          <CardDescription>
            Each pack has a SKU (unique id), scope (<em>wedding</em> = wedding-only leads;
            <em> all_events</em> = any event), quantity, and price. Vendors / pros see and
            buy these via Razorpay. Wedding scope can be consumed by wedding-only flows;
            all-events grants count for any lead.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="catalog-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-secondary border-b border-[#E8E1D5]">
                  <th className="py-2 pr-2 font-bold">SKU</th>
                  <th className="py-2 pr-2 font-bold">Label</th>
                  <th className="py-2 pr-2 font-bold">Scope</th>
                  <th className="py-2 pr-2 font-bold">Qty</th>
                  <th className="py-2 pr-2 font-bold">Price (₹)</th>
                  <th className="py-2 pr-2 font-bold"></th>
                </tr>
              </thead>
              <tbody>
                {catalog.map((row, i) => (
                  <tr key={i} className="border-b border-[#F0EAE0]" data-testid={`catalog-row-${i}`}>
                    <td className="py-1.5 pr-2">
                      <Input value={row.sku || ''}
                        onChange={(e) => updateRow(i, { sku: e.target.value })}
                        placeholder="wedding_30"
                        className="rounded-none h-8 text-xs"
                        data-testid={`catalog-sku-${i}`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input value={row.label || ''}
                        onChange={(e) => updateRow(i, { label: e.target.value })}
                        placeholder="Wedding · 30 leads"
                        className="rounded-none h-8 text-xs"
                        data-testid={`catalog-label-${i}`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <select value={row.scope}
                        onChange={(e) => updateRow(i, { scope: e.target.value })}
                        className="border border-[#E8E1D5] rounded-none h-8 text-xs px-2 bg-white w-full"
                        data-testid={`catalog-scope-${i}`}>
                        <option value="wedding">wedding</option>
                        <option value="all_events">all_events</option>
                      </select>
                    </td>
                    <td className="py-1.5 pr-2 w-[80px]">
                      <Input type="number" min={1}
                        value={row.qty}
                        onChange={(e) => updateRow(i, { qty: e.target.value })}
                        className="rounded-none h-8 text-xs"
                        data-testid={`catalog-qty-${i}`} />
                    </td>
                    <td className="py-1.5 pr-2 w-[100px]">
                      <Input type="number" min={1}
                        value={row.price_inr}
                        onChange={(e) => updateRow(i, { price_inr: e.target.value })}
                        className="rounded-none h-8 text-xs"
                        data-testid={`catalog-price-${i}`} />
                    </td>
                    <td className="py-1.5">
                      <Button variant="ghost" size="sm"
                        onClick={() => removeRow(i)}
                        className="h-8 text-rose-600 hover:bg-rose-50 rounded-none"
                        data-testid={`catalog-remove-${i}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button onClick={addRow} variant="outline" size="sm"
              className="rounded-none border-[#E8E1D5]"
              data-testid="catalog-add-row-btn">
              <Plus size={14} className="mr-1.5" /> Add pack
            </Button>
            <Button onClick={saveCatalog} disabled={savingCat}
              className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
              data-testid="catalog-save-btn">
              <FloppyDisk size={14} className="mr-1.5" /> {savingCat ? 'Saving…' : 'Save catalog'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLeadPacksPanel;
